import { add, aabbFromPoints, localToWorld, scale, sub, type Vec2 } from '@indusketch/geometry';
import { computeDocumentBounds } from './derived/bounds.js';
import { CommandError, type Command } from './history/history.js';
import { commandText, errorText } from './texts.js';
import type { Background, ProjectDocument } from './types.js';

/** Size of the background image in model meters. */
export const backgroundSize = (bg: Pick<Background, 'widthPx' | 'heightPx' | 'metersPerPixel'>) => ({
  width: bg.widthPx * bg.metersPerPixel,
  height: bg.heightPx * bg.metersPerPixel,
});

/** Corners in model coordinates (counter-clockwise, starting bottom left). */
export function backgroundCorners(bg: Background): Vec2[] {
  const { width, height } = backgroundSize(bg);
  return [
    { x: -width / 2, y: -height / 2 },
    { x: width / 2, y: -height / 2 },
    { x: width / 2, y: height / 2 },
    { x: -width / 2, y: height / 2 },
  ].map((p) => localToWorld(p, { position: bg.center, rotation: bg.rotation }));
}

/**
 * First placement of an uploaded image: centered on the planning with a plausible provisional
 * scale (about 1.5 × the planned area, at least 150 m wide) until the user calibrates it.
 */
export function initialBackground(
  doc: ProjectDocument,
  image: {
    assetId: string;
    name: string;
    widthPx: number;
    heightPx: number;
    pdf?: { assetId: string; page: number };
  },
): Background {
  const box = computeDocumentBounds(doc);
  const center = box ? { x: (box.min.x + box.max.x) / 2, y: (box.min.y + box.max.y) / 2 } : { x: 0, y: 0 };
  const span = box ? Math.max(box.max.x - box.min.x, box.max.y - box.min.y) * 1.5 : 0;
  return {
    assetId: image.assetId,
    name: image.name.slice(0, 120) || 'Hintergrund',
    widthPx: image.widthPx,
    heightPx: image.heightPx,
    center,
    rotation: 0,
    metersPerPixel: Math.max(span, 150) / image.widthPx,
    opacity: 0.8,
    visible: true,
    locked: false,
    ...(image.pdf ? { pdf: { ...image.pdf } } : {}),
  };
}

type BackgroundDoc = Pick<ProjectDocument, 'background' | 'levelBackgrounds'>;

/** Plan behind floor `level` (0 = ground floor), null if it has none. */
export function backgroundOf(doc: BackgroundDoc, level: number): Background | null {
  if (level <= 0) return doc.background;
  return doc.levelBackgrounds?.find((b) => b.level === level) ?? null;
}

/** Every plan of the document, ground floor first. */
export const allBackgrounds = (doc: BackgroundDoc): Background[] => [
  ...(doc.background ? [doc.background] : []),
  ...(doc.levelBackgrounds ?? []),
];

const copyOf = (bg: Background): Background => ({
  ...bg,
  center: { ...bg.center },
  ...(bg.pdf ? { pdf: { ...bg.pdf } } : {}),
});

/** Puts `bg` behind floor `level` (null removes that floor's plan). */
export function setBackground(bg: Background | null, level = 0): Command {
  return {
    label: commandText(bg ? 'insertBackground' : 'removeBackground'),
    apply(draft) {
      if (level <= 0) {
        draft.background = bg ? copyOf(bg) : null;
        return;
      }
      const others = (draft.levelBackgrounds ?? []).filter((b) => b.level !== level);
      const next = bg ? [...others, { ...copyOf(bg), level }] : others;
      next.sort((a, b) => a.level - b.level);
      if (next.length > 0) draft.levelBackgrounds = next;
      else delete draft.levelBackgrounds;
    },
  };
}

export type BackgroundPatch = Partial<Omit<Background, 'assetId' | 'widthPx' | 'heightPx' | 'pdf'>>;

function requireBackground(draft: BackgroundDoc, level: number): Background {
  const bg = backgroundOf(draft, level);
  if (!bg) throw new CommandError(errorText('noBackground'));
  return bg;
}

/** Position, turn and scale are protected by the lock; visibility and opacity are not. */
export function updateBackground(
  patch: BackgroundPatch,
  label = commandText('updateBackground'),
  level = 0,
): Command {
  return {
    label,
    apply(draft) {
      const bg = requireBackground(draft, level);
      const moves = patch.center !== undefined || patch.rotation !== undefined || patch.metersPerPixel !== undefined;
      if (bg.locked && patch.locked !== false && moves) throw new CommandError(errorText('backgroundLocked'));
      Object.assign(bg, patch);
    },
  };
}

/**
 * Scales the image about `anchor` (a point picked in the image) so that the picked distance
 * matches the real one; the anchor stays where it is.
 */
export function calibrateBackground(
  anchor: Vec2,
  measured: number,
  real: number,
  level = 0,
): Command {
  return {
    label: commandText('calibrateBackground'),
    apply(draft) {
      const bg = requireBackground(draft, level);
      if (bg.locked) throw new CommandError(errorText('backgroundLocked'));
      if (!(measured > 0) || !(real > 0)) throw new CommandError(errorText('lengthPositive'));
      const factor = real / measured;
      bg.metersPerPixel *= factor;
      bg.center = add(anchor, scale(sub(bg.center, anchor), factor));
    },
  };
}

/**
 * Lays the plan of floor `level` exactly over the ground floor's: same middle, turn and
 * scale, the sheets taken as equally large (plans of one building from the same drawing).
 */
export function alignBackgroundToGround(level: number): Command {
  return {
    label: commandText('alignBackground'),
    apply(draft) {
      const ground = requireBackground(draft, 0);
      const bg = requireBackground(draft, level);
      if (bg.locked) throw new CommandError(errorText('backgroundLocked'));
      bg.center = { ...ground.center };
      bg.rotation = ground.rotation;
      bg.metersPerPixel = (ground.metersPerPixel * ground.widthPx) / bg.widthPx;
    },
  };
}

/** Axis-aligned bounds of the visible plans, e.g. for "zoom to fit". */
export function backgroundBounds(doc: BackgroundDoc) {
  const corners = allBackgrounds(doc)
    .filter((b) => b.visible)
    .flatMap(backgroundCorners);
  return corners.length > 0 ? aabbFromPoints(corners) : null;
}
